/**
 * Meaning-scope filter — "has a meaning in the language I am reading in", or
 * "has one only in some other language".
 *
 * Reads a `meaningScope` field off the node, exactly the way flagFilter reads
 * `hasSvaras`. The only difference is where the field comes from: `hasSvaras`
 * is stamped by the server into the slim index, while this one is stamped by
 * stampMeaningScope below out of a small side artifact
 * (services/meaningsIndexApi) — the languages a meaning is written in cannot be
 * read off the index, which strips MEANING `data` to nothing. Keeping the shape
 * identical means the filter would not change if this fact ever moved into the
 * index proper.
 *
 * Absent means NO MATCH, never "everything". Before the artifact lands — or if
 * it fails, or if the API has not shipped yet — a hand-typed
 * /bhajans?filters=…meaning… URL yields an empty list rather than the entire
 * library. Same rule flagFilter adopted, for the same reason.
 */

import { GraphDataNode } from '../../types/graph.types';
import type { AnyGraphNode } from '../../types/graphNodes';
import type { MeaningsIndex } from '../../services/meaningsIndexApi';
import {
  FilterDefinition,
  FilterOperator,
  FilterValueType,
  FilterSuggestion,
  FilterValue,
} from '../types';

export const MEANING_SCOPE_IN = 'in';
export const MEANING_SCOPE_OTHER = 'other';
export type MeaningScope = typeof MEANING_SCOPE_IN | typeof MEANING_SCOPE_OTHER;

/**
 * Pill and suggestion labels, so a filter never renders its raw value to a
 * reader. "your language" rather than the language's name: the value is a scope,
 * not a language, and it keeps meaning the right thing after a language switch.
 */
const SCOPE_LABELS: Record<string, string> = {
  [MEANING_SCOPE_IN]: 'in your language',
  [MEANING_SCOPE_OTHER]: 'in other languages',
};

/** The node field this filter reads. */
const SCOPE_FIELD = 'meaningScope';

const scopeOf = (node: GraphDataNode): string | undefined => {
  const value = (node as unknown as Record<string, unknown>)[SCOPE_FIELD];
  return typeof value === 'string' ? value : undefined;
};

const isScope = (value: string): value is MeaningScope =>
  value === MEANING_SCOPE_IN || value === MEANING_SCOPE_OTHER;

/**
 * Copy the meanings index onto the nodes, without mutating them.
 *
 * A copy rather than an in-place write because these nodes come from the Redux
 * catalog and are shared with every other view: stamping them in place would
 * leave one language's answer attached to nodes that another language's render
 * is about to read, and the staleness would survive until the catalog refetched.
 *
 * Returns the input array unchanged when there is no index yet, so the common
 * case — no meanings artifact, or still in flight — allocates nothing.
 */
export function stampMeaningScope(
  nodes: AnyGraphNode[],
  index: MeaningsIndex | null,
): AnyGraphNode[] {
  if (!index) return nodes;
  return nodes.map((n) => {
    if (index.inLanguage.has(n.nodeId)) return { ...n, [SCOPE_FIELD]: MEANING_SCOPE_IN };
    if (index.otherLanguages.has(n.nodeId)) return { ...n, [SCOPE_FIELD]: MEANING_SCOPE_OTHER };
    return n;
  }) as AnyGraphNode[];
}

export const meaningFilter: FilterDefinition = {
  id: 'meaning',
  label: 'Meaning',
  shortLabel: 'Meaning',
  icon: 'BookOpenIcon',
  valueType: FilterValueType.String,
  supportedOperators: [FilterOperator.Is],
  defaultOperator: FilterOperator.Is,
  placeholder: 'Select a meaning scope...',

  suggestMatch(query: string, nodes: GraphDataNode[]): FilterSuggestion[] {
    const q = query.toLowerCase().trim();
    if (!q || !'meaning'.startsWith(q)) return [];

    const suggestions: FilterSuggestion[] = [];
    for (const scope of [MEANING_SCOPE_IN, MEANING_SCOPE_OTHER]) {
      const count = nodes.filter((n) => scopeOf(n) === scope).length;
      /*
       * No count, no suggestion. Six of the ten languages have no meanings at
       * all (measured on prod 2026-07-31), and the scope is unstamped entirely
       * until the artifact loads — offering a filter that can only return
       * nothing is worse than not offering it.
       */
      if (count === 0) continue;
      suggestions.push({
        filterId: 'meaning',
        label: `Meaning: ${SCOPE_LABELS[scope]}`,
        description: `${count} bhajan${count !== 1 ? 's' : ''}`,
        prefilledOperator: FilterOperator.Is,
        prefilledValue: scope,
        needsConfiguration: false,
        score: 80,
      });
    }
    return suggestions;
  },

  execute(node: GraphDataNode, operator: FilterOperator, value: FilterValue): boolean {
    const wanted = String(value);
    // An unknown scope or an operator outside supportedOperators matches
    // nothing rather than everything, so neither can silently widen a list.
    if (!isScope(wanted) || operator !== FilterOperator.Is) return false;
    return scopeOf(node) === wanted;
  },

  getDisplayValue(value: FilterValue): string {
    return SCOPE_LABELS[String(value)] ?? String(value);
  },
};
